import { useEffect, useRef, useState } from 'react';

/**
 * useInView — fires once when element enters the viewport
 */
export function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // trigger once only
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, inView];
}

/**
 * useCounter — animates integer from 0 → target when start becomes true
 */
export function useCounter(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;
    let raf;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);

  return value;
}

/**
 * useTypewriter — types an array of { text, color } lines
 */
export function useTypewriter(lines, speed = 44, startDelay = 400) {
  const [displayed, setDisplayed] = useState(() => lines.map(() => ''));
  const [currentLine, setCurrentLine] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (lines.length === 0) return;
    setDisplayed(lines.map(() => ''));

    let li = 0;
    let ci = 0;
    let timer;

    const type = () => {
      if (li >= lines.length) { setDone(true); return; }
      const text = lines[li].text;

      if (ci <= text.length) {
        const slice = text.slice(0, ci);
        setDisplayed(prev => {
          const next = [...prev];
          next[li] = slice;
          return next;
        });
        setCurrentLine(li);
        ci++;
        timer = setTimeout(type, speed);
      } else {
        ci = 0;
        li++;
        timer = setTimeout(type, 110);
      }
    };

    const init = setTimeout(type, startDelay);
    return () => { clearTimeout(init); clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { displayed, currentLine, done };
}

/**
 * useScrollSpy — tracks which section is in view based on scroll position
 */
export function useScrollSpy(ids, offset = 80) {
  const [active, setActive] = useState(ids[0] || '');

  useEffect(() => {
    const handler = () => {
      const y = window.scrollY + offset;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', handler, { passive: true });
    handler(); // run once on mount
    return () => window.removeEventListener('scroll', handler);
  }, [ids, offset]);

  return active;
}
